import { graphql } from 'gatsby';
import 'swiper/css/effect-fade';

import { Container, Section } from 'components/GridV2';
import Video from 'components/Video';
import { Borrow, Credit, Features, GrownUp, Hero, Save, Security, Spend, Testimonials } from 'components/pages/home';
import { Layout } from 'components/rebrand/Layout';
import { SignUp } from 'components/rebrand/SignUp';
import Tiers from 'components/rebrand/Tiers';
import SEO from 'components/seo';

import { IHomePageProps } from 'typescriptDefs/pages/home';

import { afTrackingByPage } from 'helpers/getAfTracking';

const Home = ({
  data: {
    page: { borrow, credit, grownup, metadata, save, security, signUp, spend, video, ...page },
  },
  pageContext: { linkCanonical, locale, pageIsMultilingual, pageName, schemaMarkupJson, variant },
  location,
}: IHomePageProps) => {
  return (
    <Layout
      location={location}
      navSettings={{ darkBg: true }}
      pageIsMultilingual={pageIsMultilingual}
      pageName={pageName}
      pageVariant={variant}
      footerDisclaimer={page.disclaimers}
    >
      <SEO
        canonical={linkCanonical}
        description={metadata.description}
        imageSrc={metadata.image?.asset?.gatsbyImageData?.images?.fallback?.src}
        keywords={metadata.keywords}
        location={location}
        multilingual={pageIsMultilingual}
        structuredData={schemaMarkupJson}
        title={metadata.title}
      />
      <Hero
        animationMp4={page.heroAnimationMp4}
        animationWebm={page.heroAnimationWebm}
        animationMp4Mobile={page.heroAnimationMp4Mobile}
        animationWebmMobile={page.heroAnimationWebmMobile}
        ctaText={page.heroCtaText}
        heading={page.heroHeading}
        carousel={page.heroCarousel}
      />
      <Video
        className="bg-warning-50"
        transcript={page.videoTranscript}
        transcriptBtnText={page.videoTranscriptBtn}
        urlDesktop={video?.urlDesktop ?? null}
        urlMobile={video?.urlMobile ?? null}
        thumbnailDesktop={video?.thumbnailDesktop ?? null}
        thumbnailMobile={video?.thumbnailMobile ?? null}
        noOverlay
      />
      <Tiers
        className="py-56 md:pb-64 md:pt-96"
        heading={page.plansHeading}
        pageName={pageName}
        tiers={page.compareTiers}
      />
      <Spend {...spend} />
      <Save {...save} />
      <Borrow {...borrow} />
      <Credit {...credit} />
      <GrownUp {...grownup} />
      <Features heading={page.featuresHeading} features={page.features} />
      <Testimonials label={page.testimonialLabel} testimonials={page.testimonials} />
      <Security {...security} />
      <Section className="py-56 md:py-80">
        <Container className="px-32 xs:px-0">
          <SignUp
            {...signUp}
            btnProps={{
              buttonLocation: 'Sign up - home',
              queryParams: afTrackingByPage('HOME', 'ustb5jdr'),
              trackEventProps: {
                category: pageName,
                label: 'Sign up',
                locale,
                location: variant,
              },
            }}
          />
        </Container>
      </Section>
    </Layout>
  );
};

export default Home;

export const queryGraphQL = graphql`
  query ($_id: String!) {
    page: sanityPageHome(_id: { eq: $_id }) {
      metadata {
        ...sanityMetadata
      }
      heroAnimationMp4 {
        asset {
          url
        }
      }
      heroAnimationWebm {
        asset {
          url
        }
      }
      heroAnimationMp4Mobile {
        asset {
          url
        }
      }
      heroAnimationWebmMobile {
        asset {
          url
        }
      }
      heroCtaText
      heroHeading: _rawHeroHeading
      heroCarousel {
        content: _rawContent
        videoMp4 {
          asset {
            url
          }
        }
        videoWebm {
          asset {
            url
          }
        }
      }
      plansHeading: _rawPlansHeading
      compareTiers {
        ...sanityTiersCompare
      }
      spend {
        cta {
          text
          registrationAdsetId
        }
        cards {
          content {
            description: _rawDescription
            heading: _rawHeading
            image {
              altText
              img {
                asset {
                  gatsbyImageData(placeholder: NONE, width: 500)
                }
              }
            }
          }
          cta {
            text
            registrationAdsetId
          }
        }
        heading: _rawHeading
      }
      save {
        content {
          heading: _rawHeading
          description: _rawDescription
          image {
            altText
            img {
              asset {
                gatsbyImageData(placeholder: NONE, width: 2000)
              }
            }
          }
        }
        cta {
          registrationAdsetId
          text
        }
        mobileImg {
          altText
          img {
            asset {
              gatsbyImageData(placeholder: NONE, width: 800)
            }
          }
        }
      }
      borrow {
        cta {
          text
          registrationAdsetId
        }
        cards {
          content {
            description: _rawDescription
            heading: _rawHeading
            image {
              altText
              img {
                asset {
                  gatsbyImageData(placeholder: NONE, width: 64)
                }
              }
            }
          }
          cta {
            text
            registrationAdsetId
          }
        }
        heading: _rawHeading
      }
      credit {
        cta {
          text
          registrationAdsetId
        }
        content {
          description: _rawDescription
          heading: _rawHeading
          image {
            altText
            img {
              asset {
                gatsbyImageData(placeholder: NONE, width: 1000)
              }
            }
          }
        }
      }
      disclaimers: _rawDisclaimers
      grownup {
        cta {
          text
          registrationAdsetId
        }
        content {
          description: _rawDescription
          heading: _rawHeading
          image {
            altText
            img {
              asset {
                gatsbyImageData(placeholder: NONE, width: 1000)
              }
            }
          }
        }
        video {
          asset {
            url
          }
        }
      }
      testimonialLabel
      testimonials {
        description: _rawDescription
        heading: _rawHeading
        image {
          altText
          img {
            asset {
              gatsbyImageData(placeholder: NONE, width: 1000)
            }
          }
        }
      }
      featuresHeading: _rawFeaturesHeading
      features {
        description: _rawDescription
        heading: _rawHeading
        image {
          altText
          img {
            asset {
              gatsbyImageData(placeholder: NONE, width: 800)
            }
          }
        }
      }
      security {
        cta {
          text
          registrationAdsetId
        }
        cards {
          content {
            description: _rawDescription
            heading: _rawHeading
            image {
              altText
              img {
                asset {
                  gatsbyImageData(placeholder: NONE, width: 64)
                }
              }
            }
          }
          cta {
            text
            registrationAdsetId
          }
        }
        heading: _rawHeading
      }
      signUp {
        ...sanitySignUp
      }
      video {
        thumbnailDesktop {
          asset {
            gatsbyImageData(placeholder: NONE, width: 2300)
          }
        }
        thumbnailMobile {
          asset {
            gatsbyImageData(placeholder: NONE, width: 800)
          }
        }
        urlDesktop
        urlMobile
      }
      videoTranscript: _rawVideoTranscript
      videoTranscriptBtn: _rawVideoTranscriptBtn
    }
  }
`;
